import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Modal, DetailRow, PageHeader, SearchBar, Pagination, DeleteConfirmModal, BtnSecondary, ActionButtons, LoadingSpinner, EmptyState } from './v2-utils';
import '../styles/v2.css';

const COLORS = ['linear-gradient(135deg,#f87171,#ef4444)','linear-gradient(135deg,#fbbf24,#d97706)','linear-gradient(135deg,#60a5fa,#3b82f6)','linear-gradient(135deg,#a78bfa,#8b5cf6)','linear-gradient(135deg,#34d399,#10b981)','linear-gradient(135deg,#f472b6,#ec4899)'];
const PAGE_SIZES = [6, 12, 18];

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()\[\]]+/).filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2);
}

export default function ViewUnitsV2() {
  const navigate = useNavigate();
  const [units, setUnits]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(6);
  const [selected, setSelected]   = useState(null);
  const [toDelete, setToDelete]   = useState(null);
  const [toast, setToast]         = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    axiosInstance.get('/units').then(r => setUnits(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  useEffect(() => { setPage(0); }, [search]);

  const filtered = units.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${u.product_name||''} ${u.brand||''} ${u.variety||''} ${u.size||''} ${u.unit_type||''}`.toLowerCase().includes(q);
  });
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated  = filtered.slice(page*pageSize, page*pageSize+pageSize);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/units/${toDelete.unit_id}`);
      setUnits(prev => prev.filter(u => u.unit_id !== toDelete.unit_id));
      setToast({ message: 'Unit deleted successfully.', severity: 'success' });
    } catch { setToast({ message: 'Failed to delete unit.', severity: 'error' }); }
    finally { setToDelete(null); }
  };

  if (loading) return <LoadingSpinner message="Loading units…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader title="Units" subtitle={`${filtered.length} unit${filtered.length!==1?'s':''}`}
        action={<button onClick={() => navigate('/new/dashboard/units/add')} className="v2-btn-submit" style={{ width:'auto', padding:'10px 20px', fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
          <svg style={{ width:15,height:15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>Add Unit</button>} />

      <SearchBar value={search} onChange={setSearch} placeholder="Search by product, brand, variety, unit type…" />

      {paginated.length === 0 ? <EmptyState message={search ? 'No units match your search.' : 'No units added yet.'} /> : isDesktop ? (
        <div className="v2-card" style={{ overflow: 'hidden' }}>
          <table className="v2-table" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #f1f5f1' }}>
                {['Product','Unit Type','Category','Compared To','Conversion','Actions'].map(h=>(
                  <th key={h} style={{ padding:'12px 20px', textAlign:h==='Product'?'left':'center', fontSize:10.5, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((u, i) => (
                <tr key={u.unit_id} style={{ borderBottom:i<paginated.length-1?'1px solid #f8faf8':'none' }}>
                  <td style={{ padding:'13px 20px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                      <div style={{ width:32, height:32, borderRadius:9, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <span style={{ color:'white', fontSize:11, fontWeight:900 }}>{getInitials(u.product_name)}</span>
                      </div>
                      <div>
                        <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#1e293b', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {u.product_name}{u.brand?` (${u.brand})`:''}
                        </p>
                        {(u.variety||u.size) && <p style={{ margin:'2px 0 0', fontSize:11, color:'#94a3b8' }}>{[u.variety,u.size].filter(Boolean).join(' · ')}</p>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, fontWeight:600, color:'#334155' }}>{u.unit_type}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center' }}>
                    <span style={{ fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:99, background:u.unit_category==='buying'?'#eff6ff':'#f0fdf4', color:u.unit_category==='buying'?'#1d4ed8':'#15803d' }}>
                      {u.unit_category}
                    </span>
                  </td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#64748b' }}>{u.opposite_unit_type||'—'}</td>
                  <td style={{ padding:'13px 20px', textAlign:'center', fontSize:13, color:'#64748b' }}>{u.conversion_rate||'—'}</td>
                  <td style={{ padding:'13px 20px' }}>
                    <ActionButtons onEdit={() => navigate(`/new/dashboard/units/edit/${u.unit_id}`)} onInfo={() => setSelected(u)} onDelete={() => setToDelete(u)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
          {paginated.map((u, i) => (
            <div key={u.unit_id} className="v2-card" style={{ padding:20, display:'flex', flexDirection:'column', gap:14, minWidth:0, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:COLORS[i%COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ color:'white', fontSize:12, fontWeight:900 }}>{getInitials(u.product_name)}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {u.product_name}{u.brand?` (${u.brand})`:''}
                  </p>
                  <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:99, background:u.unit_category==='buying'?'#eff6ff':'#f0fdf4', color:u.unit_category==='buying'?'#1d4ed8':'#15803d', marginTop:4, display:'inline-block' }}>
                    {u.unit_type} · {u.unit_category}
                  </span>
                </div>
              </div>
              {(u.variety||u.size) && <p style={{ margin:0, fontSize:12, color:'#94a3b8' }}>{[u.variety,u.size].filter(Boolean).join(' · ')}</p>}
              <div style={{ fontSize:13, color:'#64748b', display:'flex', flexDirection:'column', gap:4, borderTop:'1px solid #f1f5f1', paddingTop:12 }}>
                {u.opposite_unit_type && <p style={{ margin:0 }}>Compared to: <strong style={{ color:'#334155' }}>{u.opposite_unit_type}</strong></p>}
                {u.conversion_rate && <p style={{ margin:0 }}>Conversion: <strong style={{ color:'#334155' }}>{u.conversion_rate}</strong></p>}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setSelected(u)} style={{ flex:1, padding:'7px 0', borderRadius:9, border:'1.5px solid #e2e8f0', background:'white', color:'#475569', fontSize:12, fontWeight:600, cursor:'pointer' }}>Details</button>
                <button onClick={() => navigate(`/new/dashboard/units/edit/${u.unit_id}`)} style={{ flex:1, padding:'7px 0', borderRadius:9, border:'1.5px solid #a7f3d0', background:'#f0fdf4', color:'#065f46', fontSize:12, fontWeight:600, cursor:'pointer' }}>Edit</button>
                <button onClick={() => setToDelete(u)} style={{ flex:1, padding:'7px 0', borderRadius:9, border:'1.5px solid #fecaca', background:'#fef2f2', color:'#ef4444', fontSize:12, fontWeight:600, cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={filtered.length}
        onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(0); }} pageSizes={PAGE_SIZES} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Unit Details"
        footer={<BtnSecondary onClick={() => setSelected(null)}>Close</BtnSecondary>}>
        {selected && <>
          <DetailRow label="Product" value={`${selected.product_name||''}${selected.brand?` (${selected.brand})`:''}`} />
          {selected.variety && <DetailRow label="Variety" value={selected.variety} />}
          {selected.size && <DetailRow label="Size" value={selected.size} />}
          <DetailRow label="Unit Type" value={selected.unit_type} />
          <DetailRow label="Category" value={selected.unit_category} />
          <DetailRow label="Compared To" value={selected.opposite_unit_type} />
          <DetailRow label="Conversion Rate" value={selected.conversion_rate} />
        </>}
      </Modal>

      <DeleteConfirmModal open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={handleDelete}
        itemDescription={toDelete ? `${toDelete.unit_type} (${toDelete.unit_category}) · ${toDelete.product_name}` : ''} />

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
